<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import ButtonV4 from 'next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

const props = defineProps({
  healthData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['refresh']);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const { t } = useI18n();
const route = useRoute();
const inboxId = computed(() => route.params.inboxId);

const analyticsData = ref(null);
const isLoadingAnalytics = ref(false);
const showAnalytics = ref(true);
const dateRange = ref('7days');
const QUALITY_COLORS = {
  GREEN: 'text-n-teal-11',
  YELLOW: 'text-n-amber-11',
  RED: 'text-n-ruby-11',
  UNKNOWN: 'text-n-slate-12',
};
const STATUS_COLORS = {
  APPROVED: 'text-n-teal-11',
  PENDING_REVIEW: 'text-n-amber-11',
  AVAILABLE_WITHOUT_REVIEW: 'text-n-teal-11',
  REJECTED: 'text-n-ruby-9',
  DECLINED: 'text-n-ruby-9',
};
const MODE_COLORS = {
  LIVE: 'text-n-teal-11',
  SANDBOX: 'text-n-slate-11',
};
const healthItems = computed(() => {
  if (!props.healthData) {
    return [];
  }
  const {
    display_phone_number: displayPhoneNumber,
    verified_name: verifiedName,
    name_status: nameStatus,
    new_name_status: newNameStatus,
    quality_rating: qualityRating,
    messaging_limit_tier: messagingLimitTier,
    account_mode: accountMode,
  } = props.healthData;
  return [
    {
      key: 'displayPhoneNumber',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_PHONE_NUMBER.LABEL'),
      value: displayPhoneNumber || 'N/A',
      tooltip: t(
        'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_PHONE_NUMBER.TOOLTIP'
      ),
      show: true,
    },
    {
      key: 'verifiedName',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.VERIFIED_NAME.LABEL'),
      value: verifiedName || 'N/A',
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.VERIFIED_NAME.TOOLTIP'),
      show: true,
    },
    {
      key: 'displayNameStatus',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_NAME_STATUS.LABEL'),
      value: nameStatus || 'UNKNOWN',
      newNameStatus:
        newNameStatus && newNameStatus !== 'NONE' ? newNameStatus : null,
      tooltip: t(
        'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_NAME_STATUS.TOOLTIP'
      ),
      show: true,
      type: 'status',
    },
    {
      key: 'qualityRating',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.QUALITY_RATING.LABEL'),
      value: qualityRating || 'UNKNOWN',
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.QUALITY_RATING.TOOLTIP'),
      show: true,
      type: 'quality',
    },
    {
      key: 'messagingLimitTier',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.MESSAGING_LIMIT_TIER.LABEL'),
      value: messagingLimitTier || 'UNKNOWN',
      tooltip: t(
        'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.MESSAGING_LIMIT_TIER.TOOLTIP'
      ),
      show: true,
      type: 'tier',
    },
    {
      key: 'accountMode',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.ACCOUNT_MODE.LABEL'),
      value: accountMode || 'UNKNOWN',
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.ACCOUNT_MODE.TOOLTIP'),
      show: true,
      type: 'mode',
    },
  ];
});
const handleGoToSettings = () => {
  const { business_id: businessId } = props.healthData || {};
  if (businessId) {
    // WhatsApp Business Manager URL with specific business ID and phone numbers tab
    const whatsappBusinessUrl = `https://business.facebook.com/latest/whatsapp_manager/phone_numbers/?business_id=${businessId}&tab=phone-numbers`;
    window.open(whatsappBusinessUrl, '_blank');
  } else {
    // Fallback to general WhatsApp Business Manager if business_id is not available
    const fallbackUrl = 'https://business.facebook.com/';
    window.open(fallbackUrl, '_blank');
  }
};
const getQualityRatingTextColor = rating =>
  QUALITY_COLORS[rating] || QUALITY_COLORS.UNKNOWN;
const formatTierDisplay = tier =>
  t(`INBOX_MGMT.ACCOUNT_HEALTH.VALUES.TIERS.${tier}`) || tier;
const formatStatusDisplay = status =>
  t(`INBOX_MGMT.ACCOUNT_HEALTH.VALUES.STATUSES.${status}`) || status;
const formatModeDisplay = mode =>
  t(`INBOX_MGMT.ACCOUNT_HEALTH.VALUES.MODES.${mode}`) || mode;
const getModeStatusTextColor = mode => MODE_COLORS[mode] || 'text-n-slate-12';
const getStatusTextColor = status => STATUS_COLORS[status] || 'text-n-slate-12';

const getDateRangeFromSelection = () => {
  const now = new Date();
  const ranges = {
    '7days': { days: 7 },
    '30days': { days: 30 },
    '90days': { days: 90 },
  };

  const { days } = ranges[dateRange.value] || ranges['7days'];
  const startDate = new Date(now);
  startDate.setDate(startDate.getDate() - days);

  return {
    startDate: startDate.toISOString(),
    endDate: now.toISOString(),
  };
};

const fetchAnalytics = async () => {
  isLoadingAnalytics.value = true;
  try {
    const { startDate, endDate } = getDateRangeFromSelection();
    const response = await InboxHealthAPI.getAnalytics(
      inboxId.value,
      startDate,
      endDate
    );
    analyticsData.value = response.data;
  } catch {
    // Analytics fetch failed silently - will show empty state
  } finally {
    isLoadingAnalytics.value = false;
  }
};

const formatDuration = seconds => {
  if (!seconds) return '0s';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

const phoneMonitoringMetrics = computed(() => {
  if (!props.healthData) return [];

  const { daily_message_limit, is_flagged, status_level, can_send_messages } =
    props.healthData;

  const statusConfig = {
    high: {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.STATUS_HIGH'),
      color: 'text-n-teal-11',
      bgColor: 'bg-n-teal-3',
      icon: 'i-lucide-trending-up',
    },
    medium: {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.STATUS_MEDIUM'),
      color: 'text-n-amber-11',
      bgColor: 'bg-n-amber-3',
      icon: 'i-lucide-minus',
    },
    low: {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.STATUS_LOW'),
      color: 'text-n-ruby-11',
      bgColor: 'bg-n-ruby-3',
      icon: 'i-lucide-trending-down',
    },
    flagged: {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.STATUS_FLAGGED'),
      color: 'text-n-ruby-11',
      bgColor: 'bg-n-ruby-3',
      icon: 'i-lucide-alert-triangle',
    },
    unknown: {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.STATUS_UNKNOWN'),
      color: 'text-n-slate-11',
      bgColor: 'bg-n-slate-3',
      icon: 'i-lucide-help-circle',
    },
  };

  const currentStatus = statusConfig[status_level] || statusConfig.unknown;

  return [
    {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.PHONE_STATUS'),
      value: currentStatus.label,
      icon: currentStatus.icon,
      color: currentStatus.color,
      bgColor: currentStatus.bgColor,
      isFlagged: is_flagged,
    },
    {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.DAILY_LIMIT'),
      value:
        daily_message_limit === Infinity
          ? t('INBOX_MGMT.ACCOUNT_HEALTH.UNLIMITED')
          : daily_message_limit?.toLocaleString() || 'N/A',
      icon: 'i-lucide-send',
      color: 'text-n-blue-11',
      bgColor: 'bg-n-blue-3',
    },
    {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.SEND_STATUS'),
      value: can_send_messages
        ? t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.CAN_SEND')
        : t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.CANNOT_SEND'),
      icon: can_send_messages ? 'i-lucide-check-circle' : 'i-lucide-x-circle',
      color: can_send_messages ? 'text-n-teal-11' : 'text-n-ruby-11',
      bgColor: can_send_messages ? 'bg-n-teal-3' : 'bg-n-ruby-3',
    },
  ];
});

const analyticsMetrics = computed(() => {
  if (!analyticsData.value?.local_metrics) return [];

  const { local_metrics } = analyticsData.value;
  return [
    {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.TOTAL_CONVERSATIONS'),
      value: local_metrics.total_conversations || 0,
      icon: 'i-lucide-message-circle',
      color: 'text-n-blue-11',
    },
    {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.TOTAL_MESSAGES'),
      value: local_metrics.total_messages || 0,
      icon: 'i-lucide-messages-square',
      color: 'text-n-teal-11',
    },
    {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.AVG_FIRST_RESPONSE'),
      value: formatDuration(local_metrics.average_first_response_time || 0),
      icon: 'i-lucide-clock',
      color: 'text-n-purple-11',
    },
    {
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.AVG_RESOLUTION_TIME'),
      value: formatDuration(local_metrics.average_resolution_time || 0),
      icon: 'i-lucide-timer',
      color: 'text-n-amber-11',
    },
  ];
});

const conversationChartData = computed(() => {
  if (!analyticsData.value?.local_metrics) return null;

  const { local_metrics } = analyticsData.value;
  return {
    labels: [
      t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.OPEN'),
      t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.RESOLVED'),
    ],
    datasets: [
      {
        label: t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.CONVERSATIONS'),
        data: [
          local_metrics.open_conversations || 0,
          local_metrics.resolved_conversations || 0,
        ],
        backgroundColor: ['rgba(59, 130, 246, 0.5)', 'rgba(16, 185, 129, 0.5)'],
        borderColor: ['rgb(59, 130, 246)', 'rgb(16, 185, 129)'],
        borderWidth: 1,
      },
    ],
  };
});

const messageChartData = computed(() => {
  if (!analyticsData.value?.local_metrics) return null;

  const { local_metrics } = analyticsData.value;
  return {
    labels: [
      t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.INCOMING'),
      t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.OUTGOING'),
    ],
    datasets: [
      {
        label: t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.MESSAGES'),
        data: [
          local_metrics.incoming_messages || 0,
          local_metrics.outgoing_messages || 0,
        ],
        backgroundColor: ['rgba(139, 92, 246, 0.5)', 'rgba(236, 72, 153, 0.5)'],
        borderColor: ['rgb(139, 92, 246)', 'rgb(236, 72, 153)'],
        borderWidth: 1,
      },
    ],
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: true,
  plugins: {
    legend: {
      display: false,
    },
  },
};

const isRefreshing = ref(false);

const refreshAllData = async () => {
  isRefreshing.value = true;
  try {
    // Emit event to parent to refresh health data
    emit('refresh');
    // Also refresh analytics
    await fetchAnalytics();
  } catch {
    // Refresh failed silently
  } finally {
    isRefreshing.value = false;
  }
};

onMounted(() => {
  if (inboxId.value) {
    fetchAnalytics();
  }
});

watch(dateRange, () => {
  fetchAnalytics();
});
</script>

<template>
  <div class="gap-4 pt-8 mx-8">
    <div
      class="px-5 py-5 space-y-6 rounded-xl border shadow-sm border-n-weak bg-n-solid-2"
    >
      <div
        class="flex flex-col gap-5 justify-between items-start w-full md:flex-row"
      >
        <div>
          <span class="text-base font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.TITLE') }}
          </span>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex gap-2 flex-shrink-0">
          <ButtonV4
            sm
            outlined
            blue
            :disabled="isRefreshing"
            @click="refreshAllData"
          >
            <Icon
              :icon="isRefreshing ? 'i-lucide-loader-2' : 'i-lucide-refresh-cw'"
              :class="{ 'animate-spin': isRefreshing }"
              class="w-4 h-4 mr-1"
            />
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.REFRESH') }}
          </ButtonV4>
          <ButtonV4 sm solid blue @click="handleGoToSettings">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.GO_TO_SETTINGS') }}
          </ButtonV4>
        </div>
      </div>

      <div v-if="healthData" class="grid grid-cols-1 gap-4 xs:grid-cols-2">
        <div
          v-for="item in healthItems"
          :key="item.key"
          class="flex flex-col gap-2 p-4 rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div class="flex gap-2 items-center">
            <span class="text-sm font-medium text-n-slate-11">
              {{ item.label }}
            </span>
            <Icon
              v-tooltip.top="item.tooltip"
              icon="i-lucide-info"
              class="flex-shrink-0 w-4 h-4 cursor-help text-n-slate-9"
            />
          </div>
          <div class="flex items-center">
            <span
              v-if="item.type === 'quality'"
              class="inline-flex items-center px-2 py-0.5 min-h-6 text-xs font-medium rounded-md bg-n-alpha-2"
              :class="getQualityRatingTextColor(item.value)"
            >
              {{ item.value }}
            </span>
            <div v-else-if="item.type === 'status'" class="flex flex-col gap-1">
              <span
                class="inline-flex items-center px-2 py-0.5 min-h-6 text-xs font-medium rounded-md bg-n-alpha-2"
                :class="getStatusTextColor(item.value)"
              >
                {{ formatStatusDisplay(item.value) }}
              </span>
              <span
                v-if="item.newNameStatus"
                class="inline-flex items-center gap-1 px-2 py-0.5 min-h-6 text-xs font-medium rounded-md bg-n-alpha-2"
                :class="getStatusTextColor(item.newNameStatus)"
              >
                <Icon icon="i-lucide-clock" class="w-3 h-3" />
                {{
                  `${t('INBOX_MGMT.ACCOUNT_HEALTH.NEW_NAME_STATUS')}: ${formatStatusDisplay(item.newNameStatus)}`
                }}
              </span>
            </div>
            <span
              v-else-if="item.type === 'mode'"
              class="inline-flex items-center px-2 py-0.5 min-h-6 text-xs font-medium rounded-md bg-n-alpha-2"
              :class="getModeStatusTextColor(item.value)"
            >
              {{ formatModeDisplay(item.value) }}
            </span>
            <span
              v-else-if="item.type === 'tier'"
              class="text-sm font-medium text-n-slate-12"
            >
              {{ formatTierDisplay(item.value) }}
            </span>
            <span v-else class="text-sm font-medium text-n-slate-12">{{
              item.value
            }}</span>
          </div>
        </div>
      </div>

      <div v-else class="pt-8">
        <div
          class="flex justify-center items-center p-8 text-center text-n-slate-11"
        >
          <div>
            <Icon icon="i-lucide-activity" class="mb-2 w-8 h-8" />
            <p class="text-sm">{{ t('INBOX_MGMT.ACCOUNT_HEALTH.NO_DATA') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Phone Number Monitoring Section -->
    <div
      v-if="healthData && phoneMonitoringMetrics.length > 0"
      class="px-5 py-5 mt-6 space-y-6 rounded-xl border shadow-sm border-n-weak bg-n-solid-2"
    >
      <div>
        <div class="flex gap-2 items-center">
          <Icon icon="i-lucide-activity" class="w-5 h-5 text-n-blue-11" />
          <span class="text-base font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.TITLE') }}
          </span>
        </div>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.DESCRIPTION') }}
        </p>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div
          v-for="metric in phoneMonitoringMetrics"
          :key="metric.label"
          class="relative flex flex-col gap-3 p-5 rounded-lg border border-n-weak bg-n-solid-1 transition-all hover:shadow-md"
        >
          <!-- Flagged Badge -->
          <div v-if="metric.isFlagged" class="absolute top-2 right-2">
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-n-ruby-3 text-n-ruby-11"
            >
              <Icon icon="i-lucide-flag" class="w-3 h-3" />
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MONITORING.FLAGGED') }}
            </span>
          </div>

          <div class="flex gap-3 items-start">
            <div
              class="flex justify-center items-center p-2 rounded-lg"
              :class="metric.bgColor"
            >
              <Icon :icon="metric.icon" class="w-5 h-5" :class="metric.color" />
            </div>
            <div class="flex-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{ metric.label }}
              </span>
              <div class="mt-1 text-xl font-bold" :class="metric.color">
                {{ metric.value }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Analytics Section -->
    <div
      v-if="showAnalytics"
      class="px-5 py-5 mt-6 space-y-6 rounded-xl border shadow-sm border-n-weak bg-n-solid-2"
    >
      <div
        class="flex flex-col gap-4 justify-between items-start md:flex-row md:items-center"
      >
        <div>
          <span class="text-base font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.TITLE') }}
          </span>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.DESCRIPTION') }}
          </p>
        </div>

        <!-- Date Range Selector -->
        <div class="flex gap-2">
          <button
            v-for="range in ['7days', '30days', '90days']"
            :key="range"
            class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
            :class="
              dateRange === range
                ? 'bg-n-blue-9 text-white'
                : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
            "
            @click="dateRange = range"
          >
            {{
              t(`INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.${range.toUpperCase()}`)
            }}
          </button>
        </div>
      </div>

      <!-- Analytics Metrics Grid -->
      <div
        v-if="!isLoadingAnalytics && analyticsData"
        class="grid grid-cols-1 gap-4 xs:grid-cols-2 md:grid-cols-4"
      >
        <div
          v-for="metric in analyticsMetrics"
          :key="metric.label"
          class="flex flex-col gap-2 p-4 rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div class="flex gap-2 items-center">
            <Icon :icon="metric.icon" class="w-5 h-5" :class="metric.color" />
            <span class="text-xs font-medium text-n-slate-11">
              {{ metric.label }}
            </span>
          </div>
          <div class="text-2xl font-semibold text-n-slate-12">
            {{ metric.value }}
          </div>
        </div>
      </div>

      <!-- Charts Grid -->
      <div
        v-if="!isLoadingAnalytics && conversationChartData && messageChartData"
        class="grid grid-cols-1 gap-6 md:grid-cols-2"
      >
        <!-- Conversations Chart -->
        <div class="p-4 rounded-lg border border-n-weak bg-n-solid-1">
          <h3 class="mb-4 text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.CONVERSATION_STATUS') }}
          </h3>
          <div class="h-64">
            <Bar :data="conversationChartData" :options="chartOptions" />
          </div>
        </div>

        <!-- Messages Chart -->
        <div class="p-4 rounded-lg border border-n-weak bg-n-solid-1">
          <h3 class="mb-4 text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.MESSAGE_DIRECTION') }}
          </h3>
          <div class="h-64">
            <Bar :data="messageChartData" :options="chartOptions" />
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="isLoadingAnalytics"
        class="flex justify-center items-center p-8"
      >
        <Icon
          icon="i-lucide-loader-2"
          class="animate-spin w-8 h-8 text-n-slate-9"
        />
      </div>

      <!-- No Data State -->
      <div v-if="!isLoadingAnalytics && !analyticsData" class="pt-8">
        <div
          class="flex justify-center items-center p-8 text-center text-n-slate-11"
        >
          <div>
            <Icon icon="i-lucide-bar-chart-3" class="mb-2 w-8 h-8" />
            <p class="text-sm">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.ANALYTICS.NO_DATA') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
